/**
 * Tiny seedable PRNG + helpers, used by the `__random__` fixture
 * generator to produce reproducible WasteSense reports from a string
 * seed (the `?seed=` URL param).
 *
 * We deliberately bring our own PRNG rather than reusing `Math.random`
 * so the same `?seed=` always produces the same report, regardless of
 * which screen/component happens to render first or whether anything
 * else in the app has already pulled from the global RNG.
 *
 * Algorithm: mulberry32 — 32-bit, fast, good enough for fixture
 * generation. Combined with a small string-hash so we can seed from a
 * human-friendly token (e.g. `"crimson-falcon-7"`).
 */

export interface SeededRng {
  /** Next float in `[0, 1)`. */
  next(): number;
  /** Integer in `[min, max]` (inclusive both ends). */
  nextInt(min: number, max: number): number;
  /** Float in `[min, max)`. */
  nextFloat(min: number, max: number): number;
  /** True with probability `p` (0–1). */
  chance(p: number): boolean;
  /** Pick one element from a non-empty array. Throws on empty arrays. */
  pick<T>(items: readonly T[]): T;
  /** Pick one element with weighted probabilities. */
  pickWeighted<T>(items: readonly { value: T; weight: number }[]): T;
  /** Returns a shuffled shallow copy of `items`. */
  shuffle<T>(items: readonly T[]): T[];
}

/**
 * Build a seeded RNG from any string. Two callers passing the same
 * string will see the same sequence; an empty string is allowed.
 */
export function makeRng(seed: string): SeededRng {
  let state = hashStringToUint32(seed) || 1; // mulberry32 hates 0

  const next = (): number => {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const nextInt = (min: number, max: number): number => {
    if (max < min) throw new Error(`nextInt: max (${max}) < min (${min})`);
    return Math.floor(next() * (max - min + 1)) + min;
  };

  const nextFloat = (min: number, max: number): number => next() * (max - min) + min;

  const chance = (p: number): boolean => next() < p;

  const pick = <T>(items: readonly T[]): T => {
    if (items.length === 0) throw new Error("pick: empty array");
    return items[nextInt(0, items.length - 1)] as T;
  };

  const pickWeighted = <T>(items: readonly { value: T; weight: number }[]): T => {
    if (items.length === 0) throw new Error("pickWeighted: empty array");
    const total = items.reduce((acc, i) => acc + Math.max(0, i.weight), 0);
    if (total <= 0) return items[0]!.value;
    let roll = next() * total;
    for (const item of items) {
      roll -= Math.max(0, item.weight);
      if (roll <= 0) return item.value;
    }
    return items[items.length - 1]!.value;
  };

  const shuffle = <T>(items: readonly T[]): T[] => {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i -= 1) {
      const j = nextInt(0, i);
      [out[i], out[j]] = [out[j] as T, out[i] as T];
    }
    return out;
  };

  return { next, nextInt, nextFloat, chance, pick, pickWeighted, shuffle };
}

/**
 * 32-bit FNV-1a — small, deterministic across runtimes and stable
 * across releases of this codebase. We don't need cryptographic
 * strength; we just need "same input → same output" everywhere.
 */
function hashStringToUint32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Generate a fresh, URL-safe seed token. Used by the report list view
 * to mint a new seed each render, so each click into the random report
 * is a different scenario.
 *
 * Format: `<adjective>-<noun>-<2-digit-int>` — short enough to fit in
 * a URL bar comfortably, memorable enough that two open tabs don't
 * look identical at a glance.
 */
export function generateSeedToken(): string {
  const adjectives = [
    "amber",
    "azure",
    "brisk",
    "candid",
    "crimson",
    "dappled",
    "ember",
    "feral",
    "gilded",
    "halcyon",
    "indigo",
    "jagged",
    "keen",
    "lucid",
    "marbled",
    "noble",
    "obsidian",
    "placid",
    "quiet",
    "russet",
    "stark",
    "tactile",
    "umber",
    "verdant",
    "wistful",
    "zesty",
  ];
  const nouns = [
    "falcon",
    "harbour",
    "ridge",
    "willow",
    "comet",
    "delta",
    "ember",
    "fjord",
    "glade",
    "harvest",
    "isthmus",
    "juniper",
    "kestrel",
    "lighthouse",
    "meadow",
    "nexus",
    "orchard",
    "petal",
    "quarry",
    "reef",
    "summit",
    "thicket",
    "ursa",
    "vista",
    "wave",
    "yarrow",
  ];
  const a = adjectives[Math.floor(Math.random() * adjectives.length)] ?? "amber";
  const n = nouns[Math.floor(Math.random() * nouns.length)] ?? "falcon";
  const num = Math.floor(Math.random() * 90 + 10);
  return `${a}-${n}-${num}`;
}
